import { defineStore } from "pinia";
import App from "@bloglovin/inspire-engine/App";
import { contextualAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import { useGeneralStore } from "%/dynamic-component-editor/stores/general-store";
import { useStoryStore } from "%/dynamic-component-editor/stores/story-store";
import { applyStoryFields } from "%/dynamic-component-editor/standard-components/base-story-card/member-post-feed-actions";
import {
  REASON_SIGN_IN_REQUIRED,
  commentRefusalMessage,
  commentRefusalMessageFor,
  moderationRefusalMessage,
  reportRefusalMessage,
} from "%/helpers/story-comment-refusal";
import {
  SentCommentNotEchoedError,
  VOTE_DOWN,
  VOTE_NONE,
  VOTE_UP,
  buildBlockPersonParams,
  buildCommentEngagementEvent,
  buildCommentModerationEvent,
  buildCommentRemovalEvent,
  commentEngagementAction,
  commentFromApi,
  commentFromSentMessage,
  commentFromStoryEngagementRow,
  commentProblemOf,
  commentWithEngagement,
  editParams,
  engagementFieldsOfComment,
  inReplyOrder,
  mergeComments,
  messageOfSegments,
  nextPageOf,
  postCommentParams,
  refreshedComments,
  repliesCursorAfter,
  replyParams,
  reportParams,
  reportWasAlreadySent,
} from "%/dynamic-component-editor/helpers/story-comments.mjs";
import { StoryEngagementError, buildConfirmedEngagementFields, createEngagementQueue } from "%/dynamic-component-editor/helpers/story-engagement.mjs";
import {
  COMMENT_MODERATION_APPROVED,
  EVENT_BLOCK_USER,
  EVENT_EDIT_COMMENT,
  EVENT_POST_COMMENT,
  EVENT_REPLY_TO_COMMENT,
  EVENT_REPORT_COMMENT,
  EVENT_UNBLOCK_USER,
  MODERATION_APPROVE,
  MODERATION_BLOCK,
  MODERATION_ESCALATE,
  STORY_COMMENT_ENTITY_TYPE,
  STORY_ENGAGEMENT_APP,
  USER_BLOCKING_APP,
} from "%/dynamic-component-editor/constants/story-engagement-constants.mjs";
import { engagesNatively } from "%/dynamic-component-editor/standard-components/base-story-card/story-engagement-actions";

export { VOTE_DOWN, VOTE_NONE, VOTE_UP };

const PAGE_SIZE = 10;
const REPLY_PAGE_SIZE = 10;
const STORY_ENTITY_TYPE = "Story";
const MSG_ACTION_FAILED = "A comment action did not save; the comment was put back to what the server last confirmed";
const MSG_LOAD_FAILED = "The comments for a story did not load";
const MSG_REFRESH_FAILED = "Another reader changed a story's comments, but refreshing the comment list failed, so it shows the list from before their change";
const MSG_REPORT_FAILED = "A comment report did not save, so the reader was shown the refusal and the comment is not marked reported";
const MSG_MODERATION_FAILED = "A comment moderation did not save, so the moderator was shown the refusal and the comment was left as it was";
const MSG_ROUTE_UNKNOWN = "A comment was not sent: whether the story's comments go out as StoryEngagement events could not be worked out";
const MSG_BLOCK_FAILED = "Blocking a comment's author did not save, so their comments stay shown";
const MSG_UNBLOCK_FAILED = "Unblocking someone did not save, so their comments stay hidden";
const MSG_BLOCK_REFUSED = "Couldn't block %s. Try again.";
const MSG_UNBLOCK_REFUSED = "Couldn't unblock this person. Try again.";
const MSG_SENT_COMMENT_NOT_ECHOED = "The server took a comment, but its echo never reached this page, so the list was reloaded to show it";
const MSG_NO_COMMENT_INPUT = "Submit Comment was pressed for story %d, but no Comment Input for that story is on the page; "
  + "add a Comment Input next to it";

const composerHandles = new Map();
const commentStories = new Map();
const confirmedVotes = new Map();
let commentEngagementQueue = null;

function sendCommentEngagementRequest(kind, commentId, target, onDone, onError) {
  const store = useStoryCommentsStore();
  const action = commentEngagementAction(kind, target, confirmedVotes.get(commentId) ?? VOTE_NONE);
  const storyId = commentStories.get(commentId);
  if (action === null) {
    onDone(engagementFieldsOfComment(store.findComment(storyId, commentId)));
    return;
  }
  store.apiPost(
    `/message/${commentId}/${action}`,
    {},
    response => {
      const confirmed = response.messages?.[0];
      if (!confirmed) {
        onError(new Error(`The ${action} answer for comment ${commentId} carried no comment`));
        return;
      }
      const fields = engagementFieldsOfComment(commentFromApi(confirmed));
      confirmedVotes.set(commentId, fields.voteStatus);
      onDone(fields);
    },
    error => {
      store.thread(storyId).error = commentRefusalMessage(error);
      onError(error);
    },
  );
}

function readStoredCommentRow(commentId) {
  const engagement = App.get(STORY_ENGAGEMENT_APP);
  const pk = { id: Number(commentId) };
  return engagement.hasEntity(STORY_COMMENT_ENTITY_TYPE, pk) ? engagement.getEntity(STORY_COMMENT_ENTITY_TYPE, pk).getBasicFieldData() : null;
}

function confirmCommentEngagement(kind, commentId, target, counts) {
  const shown = engagementFieldsOfComment(useStoryCommentsStore().findComment(commentStories.get(commentId), commentId));
  const fields = buildConfirmedEngagementFields(kind, target, shown, counts);
  confirmedVotes.set(commentId, fields.voteStatus);
  return fields;
}

function sendCommentEngagementEvent(kind, commentId, target, onDone, onError) {
  const event = buildCommentEngagementEvent(kind, commentId, target, confirmedVotes.get(commentId) ?? VOTE_NONE);
  if (event === null) {
    onDone(confirmCommentEngagement(kind, commentId, target, null));
    return;
  }
  useStoryStore().triggerStoryEngagementEvent(event.eventType, event.params).then(
    () => onDone(confirmCommentEngagement(kind, commentId, target, readStoredCommentRow(commentId))),
    error => {
      useStoryCommentsStore().thread(commentStories.get(commentId)).error = commentRefusalMessage(error);
      onError(error);
    },
  );
}

function sendCommentEngagement(kind, commentId, target, onDone, onError) {
  engagesNatively(commentStories.get(commentId)).then(native => {
    const send = native ? sendCommentEngagementEvent : sendCommentEngagementRequest;
    send(kind, commentId, target, onDone, onError);
  }).catch(onError);
}

function showCommentEngagement(commentId, fields) {
  const storyId = commentStories.get(commentId);
  useStoryCommentsStore().updateComment(storyId, commentId, shown => commentWithEngagement(shown, fields));
}

function reportCommentEngagementFailure(error, context) {
  reportFailure(MSG_ACTION_FAILED, context, error);
}

function engagementQueue() {
  if (commentEngagementQueue === null) {
    commentEngagementQueue = createEngagementQueue({
      send: sendCommentEngagement,
      apply: showCommentEngagement,
      report: reportCommentEngagementFailure,
    });
  }
  return commentEngagementQueue;
}

function emptyThread() {
  return { comments: [], cursor: null, hasMore: false, loading: false, loaded: false, native: false, canModerate: false, error: "", postError: "" };
}

function refusedBeforeSending(segments) {
  const problem = commentProblemOf(segments);
  return problem === null ? null : new Error(commentRefusalMessageFor(problem));
}

function reportFailure(message, context, error) {
  // eslint-disable-next-line no-console
  console.error(message, { ...context, error: error?.response?.data ?? error?.message ?? error });
}

function refuseUnroutedComment(storyId, error) {
  reportFailure(MSG_ROUTE_UNKNOWN, { storyId }, error);
  return new Error(commentRefusalMessage(error));
}

function showCommentActionFailure(storyId, commentId, action, error) {
  useStoryCommentsStore().thread(storyId).error = commentRefusalMessage(error);
  reportFailure(MSG_ACTION_FAILED, { storyId, commentId, action }, error);
}

function showCommentCount(storyId, change) {
  const app = App.get(contextualAppName());
  if (app.hasEntity(STORY_ENTITY_TYPE, { id: Number(storyId) })) {
    const current = Number(app.getEntity(STORY_ENTITY_TYPE, { id: Number(storyId) })?.commentCount ?? 0);
    applyStoryFields(storyId, { commentCount: Math.max(0, current + change) });
  }
  const pageRecord = useStoryStore().stories?.[storyId];
  if (pageRecord) {
    pageRecord.commentCount = Math.max(0, Number(pageRecord.commentCount ?? 0) + change);
  }
}

function showServerCommentCount(storyId, changeWithoutThreadRow) {
  const storyStore = useStoryStore();
  const threadCounts = storyStore.readStoryThreadCounts(storyId);
  if (threadCounts === null) {
    showCommentCount(storyId, changeWithoutThreadRow);
    return;
  }
  const commentCount = Math.max(0, threadCounts.commentCount);
  storyStore.showStoryCounts(storyId, { commentCount }, { commentCount });
}

export const useStoryCommentsStore = defineStore("dynamic-component-editor-story-comments-store", {
  state: () => ({
    threads: {},
    replyPaging: {},
    openStoryId: 0,
    likedHeartColors: {},
    recentBlock: null,
  }),
  getters: {
    isReaderSignedIn() {
      return useStoryStore().isUserLoggedIn;
    },
    signedInReader() {
      const generalStore = useGeneralStore();
      return { id: generalStore.userId, name: generalStore.userData.first_name, avatarUrl: generalStore.userData.avatar_url };
    },
  },
  actions: {
    setLikedHeartColor(storyId, color) {
      if (storyId > 0 && color) {
        this.likedHeartColors[storyId] = color;
      }
    },
    likedHeartColor(storyId) {
      return this.likedHeartColors[storyId] ?? "";
    },
    thread(storyId) {
      if (!this.threads[storyId]) {
        this.threads[storyId] = emptyThread();
      }
      return this.threads[storyId];
    },
    openOverlay(storyId) {
      this.openStoryId = Number(storyId);
      if (!this.thread(storyId).loaded) {
        this.loadComments(storyId);
      }
    },
    closeOverlay() {
      this.openStoryId = 0;
    },
    requireSignedInReader() {
      if (this.isReaderSignedIn) {
        return true;
      }
      useStoryStore().redirectToLoginToEngage();
      return false;
    },
    loadComments(storyId, { more = false } = {}) {
      const thread = this.thread(storyId);
      const nothingMoreToLoad = more && !thread.hasMore;
      if (thread.loading || nothingMoreToLoad) {
        return;
      }
      thread.loading = true;
      thread.error = "";
      engagesNatively(storyId).then(native => {
        thread.native = native;
      });
      const params = { limit: PAGE_SIZE, ...more ? thread.cursor : {} };
      let everyCommentWasHidden = false;
      this.apiGet(
        `/story/${storyId}/comments`,
        params,
        response => {
          const page = (response.orderedConversationMessages ?? []).map(commentFromApi);
          thread.comments = more ? mergeComments(thread.comments, page) : page;
          for (const comment of page) {
            this.replyPaging[comment.id] = { cursor: repliesCursorAfter(comment.replies), loading: false, hasMore: true };
          }
          Object.assign(thread, nextPageOf(response.metadata));
          thread.canModerate = response.metadata?.viewerCanModerate === true;
          thread.loaded = true;
          everyCommentWasHidden = page.length === 0 && thread.hasMore;
        },
        error => {
          thread.error = commentRefusalMessage(error);
          reportFailure(MSG_LOAD_FAILED, { storyId }, error);
        },
        () => {
          thread.loading = false;
          if (everyCommentWasHidden) {
            this.loadComments(storyId, { more: true });
          }
        },
      );
    },
    loadReplies(storyId, comment) {
      const paging = this.replyPaging[comment.id] ?? { cursor: null, loading: false, hasMore: true };
      this.replyPaging[comment.id] = paging;
      if (paging.loading || !paging.hasMore) {
        return;
      }
      paging.loading = true;
      let everyReplyWasHidden = false;
      this.apiGet(
        `/story/${storyId}/comments/${comment.id}/replies`,
        { limit: REPLY_PAGE_SIZE, sort_strategy: "by_date_sent", sort_direction: "asc", ...paging.cursor ?? {} },
        response => {
          const replies = (response.messages ?? []).map(commentFromApi);
          this.updateComment(storyId, comment.id, shown => ({ ...shown, replies: inReplyOrder(mergeComments(shown.replies, replies)) }));
          Object.assign(paging, nextPageOf(response.metadata));
          everyReplyWasHidden = replies.length === 0 && paging.hasMore;
        },
        error => reportFailure(MSG_LOAD_FAILED, { storyId, commentId: comment.id }, error),
        () => {
          paging.loading = false;
          if (everyReplyWasHidden) {
            this.loadReplies(storyId, comment);
          }
        },
      );
    },
    findComment(storyId, commentId) {
      for (const comment of this.thread(storyId).comments) {
        if (comment.id === commentId) {
          return comment;
        }
        const reply = comment.replies.find(candidate => candidate.id === commentId);
        if (reply) {
          return reply;
        }
      }
      return null;
    },
    updateComment(storyId, commentId, change) {
      const thread = this.thread(storyId);
      thread.comments = thread.comments.map(comment => {
        if (comment.id === commentId) {
          return change(comment);
        }
        if (comment.replies.some(reply => reply.id === commentId)) {
          const replies = comment.replies.map(reply => {
            return reply.id === commentId ? change(reply) : reply;
          });
          return { ...comment, replies };
        }
        return comment;
      });
    },
    removeComment(storyId, commentId) {
      const thread = this.thread(storyId);
      thread.comments = thread.comments
          .filter(comment => comment.id !== commentId)
          .map(comment => ({
            ...comment,
            replies: comment.replies.filter(reply => reply.id !== commentId),
            replyCount: comment.replies.some(reply => reply.id === commentId && !reply.pending)
              ? Math.max(0, comment.replyCount - 1)
              : comment.replyCount,
          }));
    },
    markCommentReported(storyId, commentId) {
      this.updateComment(storyId, commentId, shown => ({ ...shown, reported: true }));
    },
    refreshLatestComments(storyId, removedCommentIds = []) {
      const thread = this.threads[storyId];
      if (!thread?.loaded) {
        return;
      }
      removedCommentIds.forEach(commentId => this.removeComment(storyId, commentId));
      this.apiGet(
        `/story/${storyId}/comments`,
        { limit: Math.max(PAGE_SIZE, thread.comments.length + 1) },
        response => {
          const page = (response.orderedConversationMessages ?? []).map(commentFromApi);
          thread.comments = refreshedComments(thread.comments, page);
          for (const comment of page) {
            this.replyPaging[comment.id] ??= { cursor: repliesCursorAfter(comment.replies), loading: false, hasMore: true };
          }
          Object.assign(thread, nextPageOf(response.metadata));
          thread.canModerate = response.metadata?.viewerCanModerate === true;
        },
        error => reportFailure(MSG_REFRESH_FAILED, { storyId }, error),
      );
    },
    readSentComment(storyId, created) {
      const realPk = App.get(STORY_ENGAGEMENT_APP).resolveTempPk(STORY_COMMENT_ENTITY_TYPE, { id: created.id }) ?? { id: created.id };
      const row = readStoredCommentRow(realPk.id);
      if (row === null) {
        reportFailure(MSG_SENT_COMMENT_NOT_ECHOED, { storyId }, null);
        this.refreshLatestComments(storyId);
        throw new SentCommentNotEchoedError(MSG_SENT_COMMENT_NOT_ECHOED, { storyId, commentId: created.id });
      }
      return commentFromStoryEngagementRow(row, this.signedInReader);
    },
    postComment(storyId, segments) {
      if (!this.requireSignedInReader()) {
        return Promise.reject(new Error(commentRefusalMessageFor(REASON_SIGN_IN_REQUIRED)));
      }
      const thread = this.thread(storyId);
      const refused = refusedBeforeSending(segments);
      thread.postError = refused?.message ?? "";
      if (refused) {
        return Promise.reject(refused);
      }
      return engagesNatively(storyId).then(
        native => {
          if (native) {
            return this.postCommentEvent(storyId, segments);
          }
          return this.postCommentRequest(storyId, segments);
        },
        error => {
          const refusal = refuseUnroutedComment(storyId, error);
          thread.postError = refusal.message;
          throw refusal;
        },
      );
    },
    postCommentEvent(storyId, segments) {
      const thread = this.thread(storyId);
      return useStoryStore().triggerStoryEngagementEvent(EVENT_POST_COMMENT, postCommentParams(storyId, segments))
          .then(params => this.readSentComment(storyId, params.comment), error => {
            thread.postError = commentRefusalMessage(error);
            throw new Error(thread.postError);
          })
          .then(comment => {
            thread.comments = mergeComments([comment], thread.comments);
            showServerCommentCount(storyId, comment.pending ? 0 : 1);
            return comment;
          });
    },
    postCommentRequest(storyId, segments) {
      const thread = this.thread(storyId);
      return new Promise((resolve, reject) => {
        this.apiPost(
          `/story/${storyId}/comments`,
          { message: messageOfSegments(segments), tempId: `comment-${storyId}-${Date.now()}` },
          response => {
            const sent = (response.modifiedMessages ?? []).map(commentFromSentMessage);
            thread.comments = mergeComments(sent, thread.comments);
            if (sent.some(comment => !comment.pending)) {
              showCommentCount(storyId, 1);
            }
            resolve(sent[0]);
          },
          error => {
            thread.postError = commentRefusalMessage(error);
            reject(new Error(thread.postError));
          },
        );
      });
    },
    postReply(storyId, parent, segments) {
      if (!this.requireSignedInReader()) {
        return Promise.reject(new Error(commentRefusalMessageFor(REASON_SIGN_IN_REQUIRED)));
      }
      const refused = refusedBeforeSending(segments);
      if (refused) {
        return Promise.reject(refused);
      }
      return engagesNatively(storyId).then(
        native => {
          if (native) {
            return this.postReplyEvent(storyId, parent, segments);
          }
          return this.postReplyRequest(storyId, parent, segments);
        },
        error => {
          throw refuseUnroutedComment(storyId, error);
        },
      );
    },
    postReplyEvent(storyId, parent, segments) {
      return useStoryStore().triggerStoryEngagementEvent(EVENT_REPLY_TO_COMMENT, replyParams(storyId, parent.id, segments))
          .then(params => this.readSentComment(storyId, params.comment), error => {
            throw new Error(commentRefusalMessage(error));
          })
          .then(reply => {
            this.updateComment(storyId, parent.id, shown => ({
              ...shown,
              replies: inReplyOrder(mergeComments(shown.replies, [reply])),
              replyCount: shown.replyCount + (reply.pending ? 0 : 1),
            }));
            showServerCommentCount(storyId, reply.pending ? 0 : 1);
            return reply;
          });
    },
    postReplyRequest(storyId, parent, segments) {
      const publicationId = useStoryStore().stories?.[storyId]?.publishingPlatformId ?? 0;
      return new Promise((resolve, reject) => {
        this.apiPost(
          `/message/${parent.id}/reply`,
          { new_message: messageOfSegments(segments), publication_id: publicationId },
          response => {
            const reply = commentFromApi(response.messages[0]);
            this.updateComment(storyId, parent.id, shown => ({
              ...shown,
              replies: inReplyOrder(mergeComments(shown.replies, [reply])),
              replyCount: shown.replyCount + (reply.pending ? 0 : 1),
            }));
            if (!reply.pending) {
              showCommentCount(storyId, 1);
            }
            resolve(reply);
          },
          error => reject(new Error(commentRefusalMessage(error))),
        );
      });
    },
    editComment(storyId, comment, segments) {
      const refused = refusedBeforeSending(segments);
      if (refused) {
        return Promise.reject(refused);
      }
      return engagesNatively(storyId).then(
        native => {
          if (native) {
            return this.editCommentEvent(storyId, comment, segments);
          }
          return this.editCommentRequest(storyId, comment, segments);
        },
        error => {
          throw refuseUnroutedComment(storyId, error);
        },
      );
    },
    editCommentEvent(storyId, comment, segments) {
      return useStoryStore().triggerStoryEngagementEvent(EVENT_EDIT_COMMENT, editParams(comment.id, segments)).then(() => {
        const row = readStoredCommentRow(comment.id);
        const pending = row === null ? comment.pending : row.moderationStatus !== COMMENT_MODERATION_APPROVED;
        this.updateComment(storyId, comment.id, shown => ({ ...shown, segments, edited: true, pending }));
        return { ...comment, segments, edited: true, pending };
      }, error => {
        throw new Error(commentRefusalMessage(error));
      });
    },
    editCommentRequest(storyId, comment, segments) {
      return new Promise((resolve, reject) => {
        this.apiPost(
          `/message/${comment.id}/edit`,
          { new_message: messageOfSegments(segments) },
          response => {
            const edited = commentFromApi(response.messages[0]);
            this.updateComment(storyId, comment.id, shown => ({ ...shown, segments: edited.segments, edited: true, pending: edited.pending }));
            resolve(edited);
          },
          error => reject(new Error(commentRefusalMessage(error))),
        );
      });
    },
    deleteComment(storyId, comment) {
      engagesNatively(storyId).then(native => {
        if (native) {
          this.deleteCommentEvent(storyId, comment);
          return;
        }
        this.deleteCommentRequest(storyId, comment);
      }).catch(error => showCommentActionFailure(storyId, comment.id, "delete", error));
    },
    deleteCommentEvent(storyId, comment) {
      const { eventType, params } = buildCommentRemovalEvent(storyId, comment, this.signedInReader.id);
      useStoryStore().triggerStoryEngagementEvent(eventType, params).then(() => {
        this.removeComment(storyId, comment.id);
        showServerCommentCount(storyId, comment.pending ? 0 : -1);
      }, error => showCommentActionFailure(storyId, comment.id, eventType, error));
    },
    deleteCommentRequest(storyId, comment) {
      this.apiPost(
        `/message/${comment.id}/delete`,
        {},
        () => {
          this.removeComment(storyId, comment.id);
          if (!comment.pending) {
            showCommentCount(storyId, -1);
          }
        },
        error => showCommentActionFailure(storyId, comment.id, "delete", error),
      );
    },
    toggleLike(storyId, comment) {
      if (!this.requireSignedInReader()) {
        return;
      }
      commentStories.set(comment.id, storyId);
      engagementQueue().tapLike(comment.id, engagementFieldsOfComment(comment));
    },
    vote(storyId, comment, direction) {
      if (!this.requireSignedInReader()) {
        return;
      }
      commentStories.set(comment.id, storyId);
      if (!confirmedVotes.has(comment.id)) {
        confirmedVotes.set(comment.id, comment.vote);
      }
      engagementQueue().tapVote(comment.id, engagementFieldsOfComment(comment), direction);
    },
    reportComment(storyId, comment, reason) {
      return engagesNatively(storyId).then(
        native => {
          if (native) {
            return this.reportCommentEvent(storyId, comment, reason);
          }
          return this.reportCommentRequest(storyId, comment, reason);
        },
        error => {
          reportFailure(MSG_REPORT_FAILED, { storyId, commentId: comment.id, reason }, error);
          throw new Error(reportRefusalMessage(error));
        },
      );
    },
    reportCommentEvent(storyId, comment, reason) {
      return useStoryStore().triggerStoryEngagementEvent(EVENT_REPORT_COMMENT, reportParams(comment.id, reason)).then(
        () => this.markCommentReported(storyId, comment.id),
        error => {
          if (reportWasAlreadySent(error)) {
            this.markCommentReported(storyId, comment.id);
            return;
          }
          reportFailure(MSG_REPORT_FAILED, { storyId, commentId: comment.id, reason }, error);
          throw new Error(reportRefusalMessage(error));
        },
      );
    },
    reportCommentRequest(storyId, comment, reason) {
      return new Promise((resolve, reject) => {
        this.apiPost(
          `/message/${comment.id}/report`,
          { reason },
          () => {
            this.markCommentReported(storyId, comment.id);
            resolve();
          },
          error => {
            if (reportWasAlreadySent(error)) {
              this.markCommentReported(storyId, comment.id);
              resolve();
              return;
            }
            reportFailure(MSG_REPORT_FAILED, { storyId, commentId: comment.id, reason }, error);
            reject(new Error(reportRefusalMessage(error)));
          },
        );
      });
    },
    moderateComment(storyId, comment, action) {
      return engagesNatively(storyId).then(native => {
        const event = buildCommentModerationEvent(storyId, comment, action);
        if (native) {
          return this.moderateCommentEvent(storyId, comment, action, event);
        }
        return this.moderateCommentRequest(storyId, comment, action);
      }).catch(error => {
        reportFailure(MSG_MODERATION_FAILED, { storyId, commentId: comment.id, action }, error);
        throw new StoryEngagementError(moderationRefusalMessage(error), { storyId, commentId: comment.id, action });
      });
    },
    moderateCommentEvent(storyId, comment, action, { eventType, params }) {
      return useStoryStore().triggerStoryEngagementEvent(eventType, params).then(() => {
        this.applyCommentModeration(storyId, comment, action);
        showServerCommentCount(storyId, action === MODERATION_APPROVE ? 1 : 0);
      });
    },
    moderateCommentRequest(storyId, comment, action) {
      return new Promise((resolve, reject) => {
        this.apiPost(
          `/message/${comment.id}/${action}`,
          { reason: "" },
          () => {
            this.applyCommentModeration(storyId, comment, action);
            if (action === MODERATION_APPROVE) {
              showCommentCount(storyId, 1);
            }
            resolve();
          },
          reject,
        );
      });
    },
    applyCommentModeration(storyId, comment, action) {
      if (action === MODERATION_BLOCK) {
        this.removeComment(storyId, comment.id);
        return;
      }
      if (action === MODERATION_ESCALATE) {
        this.updateComment(storyId, comment.id, shown => ({ ...shown, escalated: true }));
        return;
      }
      this.updateComment(storyId, comment.id, shown => ({ ...shown, pending: false, escalated: false }));
      if (comment.parentId > 0) {
        this.updateComment(storyId, comment.parentId, parent => ({ ...parent, replyCount: parent.replyCount + 1 }));
      }
    },
    triggerUserBlockingEvent(eventType, params) {
      return new Promise((resolve, reject) => {
        const fired = App.get(USER_BLOCKING_APP).triggerEventWithOutcome(eventType, params);
        fired.serverOutcome.then(rejection => {
          if (rejection) {
            reject(rejection.error);
            return;
          }
          resolve(fired.params);
        });
      });
    },
    blockAuthor(storyId, comment) {
      if (!this.requireSignedInReader()) {
        return Promise.resolve(false);
      }
      const person = { userId: comment.authorId, name: comment.authorName || "this person" };
      this.recentBlock = { ...person, storyId };
      return this.triggerUserBlockingEvent(EVENT_BLOCK_USER, buildBlockPersonParams(person.userId)).then(() => {
        this.reloadLoadedCommentThreads();
        return true;
      }, error => {
        reportFailure(MSG_BLOCK_FAILED, { storyId, blockedUserId: person.userId }, error);
        this.recentBlock = null;
        this.thread(storyId).error = MSG_BLOCK_REFUSED.replace("%s", person.name);
        return false;
      });
    },
    unblockPerson(person) {
      return this.triggerUserBlockingEvent(EVENT_UNBLOCK_USER, buildBlockPersonParams(person.userId)).then(() => {
        if (this.recentBlock?.userId === person.userId) {
          this.recentBlock = null;
        }
        this.reloadLoadedCommentThreads();
        return true;
      }, error => {
        reportFailure(MSG_UNBLOCK_FAILED, { unblockedUserId: person.userId }, error);
        throw new StoryEngagementError(MSG_UNBLOCK_REFUSED, { unblockedUserId: person.userId });
      });
    },
    undoRecentBlock() {
      const block = this.recentBlock;
      if (!block) {
        return Promise.resolve(false);
      }
      return this.unblockPerson(block).catch(error => {
        this.thread(block.storyId).error = error.message;
        return false;
      });
    },
    dismissRecentBlock() {
      this.recentBlock = null;
    },
    reloadLoadedCommentThreads() {
      Object.entries(this.threads)
          .filter(([, thread]) => thread.loaded)
          .forEach(([storyId]) => this.loadComments(Number(storyId)));
    },
    applyBlockChanges({ blocked, unblocked }) {
      if (unblocked.includes(this.recentBlock?.userId)) {
        this.recentBlock = null;
      }
      if (blocked.length > 0 || unblocked.length > 0) {
        this.reloadLoadedCommentThreads();
      }
    },
    registerCommentInput(storyId, handle) {
      composerHandles.set(storyId, handle);
      return () => {
        if (composerHandles.get(storyId) === handle) {
          composerHandles.delete(storyId);
        }
      };
    },
    openCommentInputOnPage(storyId) {
      const handle = composerHandles.get(storyId);
      if (!handle) {
        return false;
      }
      handle.reveal();
      return true;
    },
    clearComposer(storyId) {
      composerHandles.get(storyId)?.clear();
    },
    submitComposer(storyId) {
      if (!this.requireSignedInReader()) {
        return Promise.resolve(false);
      }
      const handle = composerHandles.get(storyId);
      if (!handle) {
        throw new Error(MSG_NO_COMMENT_INPUT.replace("%d", storyId));
      }
      return handle.submit();
    },
  },
});
